// The 4×4 pads (3×4 when they play the atmos kit): rubbery, glowing, bouncy (Studio skin). Mouse/touch velocity from the vertical
// hit position (top = louder), Force Touch where available.
import { useEffect, useState } from "react";
import { noteLabel } from "./gridMath";
import { clickVelocity, PAD_KEY_LABELS } from "../input/keymap";
import { padDown, padListeners, padNotes, padUp } from "./padInput";
import { useEditor } from "./store";
import { FIRST_ATMOS } from "@eb/shared";

const PAD_COLOURS = ["#e69f00", "#56b4e9", "#009e73", "#f0e442", "#0072b2", "#d55e00", "#cc79a7", "#9ad0f5"];
/** The atmos kit's pads: lavender to sky, soft. */
const ATMOS_COLOURS = ["#9b8cf2", "#b3a6ff", "#8fb8f0", "#a9d0f5"];

export function Pads() {
  const spec = useEditor((s) => s.spec);
  const target = useEditor((s) => s.padTarget);
  const octave = useEditor((s) => s.padOctave);
  const [lit, setLit] = useState<Record<number, number>>({});
  useEffect(() => {
    const l = (pad: number, on: boolean, velocity: number) => setLit((x) => ({ ...x, [pad]: on ? velocity : 0 }));
    padListeners.add(l);
    return () => void padListeners.delete(l);
  }, []);
  const atmos = target === FIRST_ATMOS;
  const notes = target >= 16 && !atmos ? padNotes(target, octave) : null;
  // Pad 1 is bottom-left: render rows top (13–16) to bottom (1–4); the atmos kit is 3 × 4.
  const order = atmos ? [8, 9, 10, 11, 4, 5, 6, 7, 0, 1, 2, 3] : [12, 13, 14, 15, 8, 9, 10, 11, 4, 5, 6, 7, 0, 1, 2, 3];
  return (
    <div className={`pads${atmos ? " pads-atmos" : ""}`} role="group" aria-label={atmos ? "Atmos pads" : "Pads"}>
      {order.map((pad) => {
        const name = notes
          ? noteLabel(notes[pad])
          : (spec?.sounds[atmos ? FIRST_ATMOS + pad : pad]?.name ?? `Pad ${pad + 1}`);
        const colour = atmos
          ? ATMOS_COLOURS[pad % ATMOS_COLOURS.length]
          : notes
            ? target === 16
              ? "#e69f00"
              : target === 17
                ? "#56b4e9"
                : "#cc79a7"
            : PAD_COLOURS[pad % PAD_COLOURS.length];
        const v = lit[pad] ?? 0;
        return (
          <button
            key={pad}
            type="button"
            className={`pad${v > 0 ? " hit" : ""}`}
            style={{ "--pad": colour, "--glow": (v / 127).toFixed(2) } as React.CSSProperties}
            aria-label={`Pad ${pad + 1}: ${name} (key ${PAD_KEY_LABELS[pad]})`}
            data-testid={`pad-${pad}`}
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              const r = e.currentTarget.getBoundingClientRect();
              const force = (e.nativeEvent as PointerEvent & { webkitForce?: number }).webkitForce;
              void padDown(pad, clickVelocity((e.clientY - r.top) / r.height, force));
            }}
            onPointerUp={() => void padUp(pad)}
            onPointerCancel={() => void padUp(pad)}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && !e.repeat) {
                e.preventDefault();
                void padDown(pad, 100, "focus");
              }
            }}
            onKeyUp={(e) => {
              if (e.key === "Enter" || e.key === " ") void padUp(pad, "focus");
            }}
          >
            <span className="pad-key">{PAD_KEY_LABELS[pad]}</span>
            <span className="pad-name">{name}</span>
          </button>
        );
      })}
    </div>
  );
}
